import React, {FC, useCallback} from "react";
import classnames from "classnames";
import type {TableProps} from "antd";
import i18n from "../../services/i18n";
import css from "./SortIconOnlyHeaderCell.less";

// AntD-internes Markup: der Caret-Block (Sortier-Icon) innerhalb der Header-Zelle.
// Bewusst an EINER Stelle gebündelt, damit ein AntD-Upgrade nur hier nachgezogen
// werden muss. Wird auf AntD 4.24 gegen useSorter.js verifiziert.
export const SORTER_SELECTOR = ".ant-table-column-sorter";

/**
 * Tooltip-Props für den Sortier-Caret („Zum Sortieren klicken"). Wird an
 * `showSorterTooltip` der jeweiligen Table übergeben.
 *
 * Hintergrund: Seit dem Sort-Icon-Only-Umbau sortiert der Header-TEXT nicht mehr
 * — der Caret ist der einzige Sort-Trigger. Dieser Tooltip macht das auffindbar
 * (ersetzt das vorher überall gesetzte `showSorterTooltip={false}`).
 *
 * AntD wrappt zwar den GESAMTEN `.ant-table-column-sorters`-Block (Titel + Caret)
 * in den Tooltip; in den DnD-Header-Zellen sind Titel/Sorter-Wrapper aber
 * `pointer-events:none` und nur der Caret `pointer-events:auto`. Über die
 * onMouseEnter-Delegation triggert der Tooltip dadurch faktisch NUR über dem
 * Caret, nicht über dem (nicht sortierenden) Titel.
 *
 * i18n wird bewusst ERST beim Aufruf gelesen (Funktion statt Modul-Level-`const`),
 * um den bekannten react-hot-loader-Crash bei Modul-Level-i18n-Proxys zu vermeiden.
 */
export const getSortIconTooltipProps = (): {title: string} => ({
    title: i18n.views.TableInteraction.clickToSort(),
});

type HeaderCellProps = React.ThHTMLAttributes<HTMLTableCellElement>;

/**
 * Hook, der das von AntD durchgereichte `onClick` (triggerSorter) in einen
 * "Klick-Gate"-Handler verpackt: Die Sortierung wird NUR ausgelöst, wenn der
 * Klick im Caret-Bereich (`SORTER_SELECTOR`) lag — ein Klick auf den Titeltext
 * oder die restliche Zelle wird verschluckt.
 *
 * Wiederverwendbar, damit auch eine DnD-fähige Header-Zelle exakt dieselbe
 * Gate-Logik nutzen kann.
 *
 * @param onClick - Das von AntD via `components.header.cell` durchgereichte
 *   `onClick`. Ist es keine Funktion, ist die Spalte nicht sortierbar.
 * @returns Einen stabilen (memoisierten) Handler, oder `undefined`, wenn kein
 *   sortierbares `onClick` vorliegt (Signal "nicht sortierbar").
 */
export const useSortIconOnlyClickGate = (
    onClick?: React.MouseEventHandler<HTMLTableCellElement>,
): React.MouseEventHandler<HTMLTableCellElement> | undefined => {
    // Stabiler Handler über `[onClick]`, damit `memo`-Tabellen nicht durch eine
    // bei jedem Render neu erzeugte Funktion unnötig re-rendern.
    const gatedHandler = useCallback<React.MouseEventHandler<HTMLTableCellElement>>(
        (event) => {
            // Defensiv: Falls `onClick` zwischen Render und Klick wegfällt.
            if (typeof onClick !== "function") {
                return;
            }

            const target = event.target as HTMLElement | null;

            if (target && typeof target.closest === "function" && target.closest(SORTER_SELECTOR)) {
                onClick(event);
            }
            // sonst: Klick auf Titel/Zelle -> KEINE Sortierung
        },
        [onClick],
    );

    // Nicht sortierbar -> kein Handler (Signal an den Aufrufer).
    return typeof onClick === "function" ? gatedHandler : undefined;
};

/**
 * Header-Zelle, die die Sortierung NUR beim Klick auf das Sortier-Icon (Caret)
 * auslöst — NICHT beim Klick auf den Spaltentitel.
 *
 * Hintergrund: AntD (4.24) hängt bei sortierbaren Spalten über `onHeaderCell`
 * ein `onClick` an die gesamte Header-Zelle, das `triggerSorter` aufruft. Dieses
 * `onClick` wird via `components.header.cell` an diese Komponente durchgereicht.
 * Wir rufen es nur auf, wenn der Klick im Caret-Bereich lag; ein Klick auf den
 * Titeltext wird verschluckt. `onKeyDown` (ENTER-Sortierung, Tastatur-a11y) wird
 * unverändert durchgereicht.
 *
 * Nicht-sortierbare Header-Zellen erhalten von AntD kein `onClick` und werden
 * deshalb als schlichtes <th> gerendert (komplett unverändertes Verhalten).
 */
export const SortIconOnlyHeaderCell: FC<HeaderCellProps> = (props) => {
    const {onClick, className, ...rest} = props;

    // Hook unbedingt VOR dem early-return aufrufen (Rules of Hooks).
    const gatedHandler = useSortIconOnlyClickGate(onClick);

    // Nicht sortierbar -> unverändert durchreichen, niemals etwas verschlucken.
    if (typeof onClick !== "function") {
        return <th className={className} {...rest} />;
    }

    return <th className={classnames(className, css.sortIconOnly)} {...rest} onClick={gatedHandler} />;
};

type TableComponentsOf<RecordType> = NonNullable<TableProps<RecordType>["components"]>;

/**
 * Mischt die SortIconOnlyHeaderCell in ein (optional bestehendes) `components`-
 * Objekt, ohne andere Overrides (z. B. `body.row` in der Zahlungsliste) zu
 * verlieren. IMMER beim Verdrahten verwenden statt `components` direkt zu setzen,
 * damit bestehende `components`-Objekte nicht versehentlich überschrieben werden.
 *
 * Generisch über den Record-Typ, damit das Ergebnis ohne Cast an `<Table<T>>`
 * jeder Liste zuweisbar ist (RecordType wird i. d. R. zu `unknown` inferiert und
 * ist dann zu jedem `TableComponents<T>` zuweisbar).
 */
export const getListTableComponents = <RecordType,>(existing?: TableComponentsOf<RecordType>): TableComponentsOf<RecordType> => ({
    ...existing,
    header: {
        ...(existing && existing.header),
        cell: SortIconOnlyHeaderCell,
    },
});

export default SortIconOnlyHeaderCell;
